import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/navigation/Sidebar';
import NavigationBreadcrumb from '../../components/navigation/NavigationBreadcrumb';
import NotificationIndicator from '../../components/navigation/NotificationIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FolderTree from './components/FolderTree';
import DocumentList from './components/DocumentList';
import UploadModal from './components/UploadModal';
import TemplateModal from './components/TemplateModal';
import DocumentPreview from './components/DocumentPreview';
import SearchBar from './components/SearchBar';

const DocumentManagement = () => {
  const [language, setLanguage] = useState('fr');
  const [direction, setDirection] = useState('ltr');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState('root');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isMobileFolderOpen, setIsMobileFolderOpen] = useState(false);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'fr';
    const savedDirection = localStorage.getItem('direction') || 'ltr';
    setLanguage(savedLanguage);
    setDirection(savedDirection);
    document.documentElement?.setAttribute('dir', savedDirection);
  }, []);

  const pageTitle = {
    en: 'Document Management',
    fr: 'Gestion des documents',
    ar: 'إدارة المستندات'
  };

  const uploadLabel = {
    en: 'Upload',
    fr: 'Télécharger',
    ar: 'تحميل'
  };

  const newTemplateLabel = {
    en: 'New Template',
    fr: 'Nouveau modèle',
    ar: 'نموذج جديد'
  };

  const foldersLabel = {
    en: 'Folders',
    fr: 'Dossiers',
    ar: 'المجلدات'
  };

  const storageLabel = {
    en: 'Storage',
    fr: 'Stockage',
    ar: 'التخزين'
  };

  const usedLabel = {
    en: 'used of',
    fr: 'utilisé sur',
    ar: 'مستخدم من'
  };

  const handleUpload = (uploadData) => {
    console.log('Upload files:', uploadData);
  };

  const handleGenerateTemplate = (templateData) => {
    console.log('Generate template:', templateData);
  };

  const handleDocumentSelect = (document) => {
    setSelectedDocument(document);
    setIsPreviewOpen(true);
  };

  const handleSearch = (searchData) => {
    console.log('Search:', searchData);
  };

  return (
    <div className="min-h-screen bg-background" style={{ direction }}>
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <div
        className={`transition-all duration-250 pt-16 lg:pt-0 ${
          sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'
        }`}
      >
        <header className="sticky top-0 z-40 bg-card border-b border-border shadow-elevation-sm">
          <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4">
            <div className="flex-1">
              <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-2">
                {pageTitle?.[language]}
              </h1>
              <NavigationBreadcrumb language={language} />
            </div>
            <div className="flex items-center gap-3">
              <NotificationIndicator language={language} />
              <Button
                variant="outline"
                size="sm"
                iconName="Upload"
                iconPosition="left"
                onClick={() => setIsUploadModalOpen(true)}
                className="hidden md:flex"
              >
                {uploadLabel?.[language]}
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="FileText"
                iconPosition="left"
                onClick={() => setIsTemplateModalOpen(true)}
                className="hidden md:flex"
              >
                {newTemplateLabel?.[language]}
              </Button>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6">
          <div className="mb-6">
            <SearchBar language={language} onSearch={handleSearch} />
          </div>

          <div className="flex gap-3 mb-6 md:hidden">
            <Button
              variant="outline"
              size="sm"
              iconName="Upload"
              iconPosition="left"
              onClick={() => setIsUploadModalOpen(true)}
              fullWidth
            >
              {uploadLabel?.[language]}
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="FileText"
              iconPosition="left"
              onClick={() => setIsTemplateModalOpen(true)}
              fullWidth
            >
              {newTemplateLabel?.[language]}
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-foreground">
                    {foldersLabel?.[language]}
                  </h3>
                  <button
                    className="lg:hidden p-2 hover:bg-muted rounded-lg transition-colors"
                    onClick={() => setIsMobileFolderOpen(!isMobileFolderOpen)}
                    aria-label="Toggle folders"
                  >
                    <Icon name={isMobileFolderOpen ? 'ChevronUp' : 'ChevronDown'} size={18} />
                  </button>
                </div>
                <div className={`${isMobileFolderOpen ? 'block' : 'hidden'} lg:block`}>
                  <FolderTree
                    language={language}
                    onFolderSelect={setSelectedFolder}
                    selectedFolder={selectedFolder}
                  />
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="text-sm font-semibold text-foreground mb-3">
                  {storageLabel?.[language]}
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>45.8 GB {usedLabel?.[language]} 100 GB</span>
                    <span>45.8%</span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-500"
                      style={{ width: '45.8%' }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="bg-card border border-border rounded-lg p-4 md:p-6 min-h-[600px]">
                <DocumentList
                  language={language}
                  selectedFolder={selectedFolder}
                  onDocumentSelect={handleDocumentSelect}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
      <UploadModal
        language={language}
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUpload}
      />
      <TemplateModal
        language={language}
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        onGenerate={handleGenerateTemplate}
      />
      {isPreviewOpen && (
        <DocumentPreview
          language={language}
          document={selectedDocument}
          onClose={() => {
            setIsPreviewOpen(false);
            setSelectedDocument(null);
          }}
        />
      )}
    </div>
  );
};

export default DocumentManagement;